import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Link, Navigate } from "react-router-dom";
import api from "../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Crown, LogOut } from "lucide-react";
import { useLang } from "../contexts/LangContext";

export default function AreaUtente() {
  const { user, logout } = useAuth();
  const { t, lang } = useLang();
  const [orders, setOrders] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [subs, setSubs] = useState([]);
  const [club, setClub] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/orders/mine").then(r => setOrders(r.data)).catch(() => {});
    api.get("/recipes/mine").then(r => setRecipes(r.data)).catch(() => {});
    api.get("/subscriptions/mine").then(r => setSubs(r.data)).catch(() => {});
    api.get("/club/me").then(r => setClub(r.data)).catch(() => {});
  }, [user]);

  if (!user) return <Navigate to="/login" replace />;

  const localeMap = { it: "it-IT", en: "en-GB", de: "de-DE", fr: "fr-FR", es: "es-ES", pt: "pt-PT" };
  const dateLocale = localeMap[lang] || "en-GB";

  return (
    <div data-testid="area-utente-page" className="container-xl py-12">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--primary))] font-semibold mb-2">{t("account.eyebrow")}</div>
          <h1 className="editorial-heading text-5xl md:text-6xl">{t("account.greeting")} {user.name}</h1>
          <div className="text-muted-foreground mt-2">{t("account.role_label")}: <span className="chip bg-[hsl(var(--muted))]">{user.role}</span></div>
        </div>
        <button
          data-testid="area-utente-logout"
          onClick={logout}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-sm border border-border text-foreground hover:bg-destructive hover:text-white hover:border-destructive transition-colors text-sm font-semibold"
        >
          <LogOut className="h-4 w-4" />
          {t("account.logout")}
        </button>
      </div>

      {club?.active && (
        <div data-testid="au-club-card" className="mt-6 bg-gradient-to-r from-[#0a0a0a] to-[#1a0608] border border-[#C9A961]/40 rounded-sm p-6 flex flex-wrap items-center gap-6 justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-[#C9A961]/10 border border-[#C9A961]/30">
              <Crown className="h-6 w-6 text-[#C9A961]" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9A961]">{t("nav.club")}</div>
              <div className="text-white text-xl editorial-heading">{t("account.club_member")} {club.tier?.name}</div>
              <div className="text-white/60 text-sm mt-0.5">{t("account.club_valid_until")} {new Date(club.membership?.expires_at).toLocaleDateString(dateLocale, { day: "numeric", month: "long", year: "numeric" })}</div>
            </div>
          </div>
          <Link to="/club" className="px-5 py-2.5 border border-[#C9A961] text-[#C9A961] hover:bg-[#C9A961] hover:text-black text-xs uppercase tracking-[0.25em] transition-colors">
            {t("account.club_explore_events")} →
          </Link>
        </div>
      )}
      {!club?.active && (
        <div className="mt-6 bg-card border border-border rounded-sm p-6 flex flex-wrap items-center gap-6 justify-between">
          <div>
            <div className="editorial-heading text-xl">{t("account.club_join_title")}</div>
            <div className="text-muted-foreground text-sm">{t("account.club_join_sub")}</div>
          </div>
          <Link to="/club" data-testid="au-join-club" className="px-5 py-2.5 bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90 text-xs uppercase tracking-[0.25em]">
            {t("account.club_join_cta")} →
          </Link>
        </div>
      )}

      <Tabs defaultValue="orders" className="mt-8">
        <TabsList>
          <TabsTrigger data-testid="tab-orders" value="orders">{t("account.tab_orders")}</TabsTrigger>
          <TabsTrigger data-testid="tab-recipes" value="recipes">{t("account.tab_recipes")}</TabsTrigger>
          <TabsTrigger data-testid="tab-subs" value="subs">{t("account.tab_subs")}</TabsTrigger>
          <TabsTrigger data-testid="tab-profile" value="profile">{t("account.tab_profile")}</TabsTrigger>
        </TabsList>
        <TabsContent value="orders" className="mt-6">
          {orders.length === 0 ? <div className="text-muted-foreground">{t("account.no_orders")}</div> : (
            <div className="space-y-3">
              {orders.map(o => (
                <div key={o.id} data-testid={`order-${o.id}`} className="bg-card border border-border rounded-sm p-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="font-semibold">{t("account.order_n")} #{o.id.substring(0, 8)}</div>
                      <div className="text-sm text-muted-foreground">{new Date(o.created_at).toLocaleString(dateLocale)}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold">€{o.total.toFixed(2)}</div>
                      <div className="chip bg-[hsl(var(--muted))]">{o.status}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
        <TabsContent value="recipes" className="mt-6">
          {recipes.length === 0 ? <div className="text-muted-foreground">{t("account.no_recipes")} <Link to="/ai-ricette" className="underline text-[hsl(var(--primary))]">{t("account.generate_first")}</Link></div> : (
            <div className="grid md:grid-cols-3 gap-4">
              {recipes.map(r => (
                <div key={r.id} className="bg-card border border-border rounded-sm p-4">
                  <div className="text-xs text-muted-foreground uppercase tracking-wider">{r.region}</div>
                  <div className="editorial-heading text-xl mt-1">{r.title}</div>
                  <div className="text-sm text-muted-foreground mt-1 line-clamp-2">{r.description}</div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
        <TabsContent value="subs" className="mt-6">
          {subs.length === 0 ? <div className="text-muted-foreground">{t("account.no_subs")}</div> : (
            <div className="space-y-3">
              {subs.map(s => (
                <div key={s.id} className="bg-card border border-border rounded-sm p-4 flex justify-between">
                  <div><div className="font-semibold">{t("account.plan")} {s.plan}</div><div className="text-sm text-muted-foreground">{t("account.every_n_days").replace("{n}", s.frequency_days)}</div></div>
                  <span className="chip bg-[hsl(var(--secondary))] text-white">{s.active ? t("account.active") : t("account.suspended")}</span>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
        <TabsContent value="profile" className="mt-6">
          <div className="bg-card border border-border rounded-sm p-6 space-y-2 max-w-lg">
            <div><span className="text-xs uppercase tracking-wider text-muted-foreground">{t("account.profile_email")}:</span> {user.email}</div>
            <div><span className="text-xs uppercase tracking-wider text-muted-foreground">{t("account.profile_name")}:</span> {user.name}</div>
            <div><span className="text-xs uppercase tracking-wider text-muted-foreground">{t("account.profile_country")}:</span> {user.country || "—"}</div>
            <div><span className="text-xs uppercase tracking-wider text-muted-foreground">{t("account.profile_lang")}:</span> {user.lang || "it"}</div>
            {user.company && <div><span className="text-xs uppercase tracking-wider text-muted-foreground">{t("account.profile_company")}:</span> {user.company}</div>}
            {user.vat_number && <div><span className="text-xs uppercase tracking-wider text-muted-foreground">{t("account.profile_vat")}:</span> {user.vat_number}</div>}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
